/**
 * Mock User Data
 * Sample users for development and testing
 * Aligned with new role hierarchy: owner, admin, partner, assistant, staff, client
 */

import type { User, UserRole, UserInvitation } from '@/types/user.types'
import type { AuthUser } from '@/types/auth.types'
import { soloTenant, mediumTenant, largeTenant } from './mockTenants'

// ============================================================================
// MOCK USERS - SOLO TENANT (1 user)
// ============================================================================

export const soloOwner: User = {
  userId: 'user-solo-001',
  email: 'rajesh.kumar@rkassociates.in',
  username: 'rajesh.kumar',
  fullName: 'Rajesh Kumar',
  firstName: 'Rajesh',
  lastName: 'Kumar',
  phone: '9876543210',
  avatarUrl: undefined,
  tenantId: soloTenant.tenantId,
  tenantName: soloTenant.tenantName,
  role: 'owner',
  userCategory: 'ca_staff',
  isActive: true,
  isVerified: true,
  failedLogins: 0,
  lastLoginAt: '2024-12-20T09:30:00Z',
  preferences: {
    theme: 'light',
    language: 'en',
    timezone: 'Asia/Kolkata',
    notifications: { email: true, push: true, sms: true },
  },
  createdAt: '2024-01-15T10:30:00Z',
  updatedAt: '2024-12-20T09:30:00Z',
}

// ============================================================================
// MOCK USERS - MEDIUM TENANT (up to 15 users)
// ============================================================================

export const mediumOwner: User = {
  userId: 'user-medium-001',
  email: 'anil.sharma@sharmapatel.co.in',
  username: 'anil.sharma',
  fullName: 'Anil Sharma',
  firstName: 'Anil',
  lastName: 'Sharma',
  phone: '9988776655',
  avatarUrl: undefined,
  tenantId: mediumTenant.tenantId,
  tenantName: mediumTenant.tenantName,
  role: 'owner',
  userCategory: 'ca_staff',
  isActive: true,
  isVerified: true,
  failedLogins: 0,
  lastLoginAt: '2024-12-20T10:00:00Z',
  preferences: { theme: 'dark', language: 'en', timezone: 'Asia/Kolkata' },
  createdAt: '2023-06-01T09:00:00Z',
  updatedAt: '2024-12-20T10:00:00Z',
}

export const mediumAdmin: User = {
  userId: 'user-medium-002',
  email: 'priya.patel@sharmapatel.co.in',
  username: 'priya.patel',
  fullName: 'Priya Patel',
  firstName: 'Priya',
  lastName: 'Patel',
  phone: '9876512345',
  avatarUrl: undefined,
  tenantId: mediumTenant.tenantId,
  tenantName: mediumTenant.tenantName,
  role: 'admin',
  userCategory: 'ca_staff',
  isActive: true,
  isVerified: true,
  failedLogins: 0,
  lastLoginAt: '2024-12-19T16:30:00Z',
  preferences: { theme: 'system', language: 'en', timezone: 'Asia/Kolkata' },
  createdAt: '2023-06-15T10:00:00Z',
  updatedAt: '2024-12-19T16:30:00Z',
}

export const mediumPartner1: User = {
  userId: 'user-medium-003',
  email: 'vikram.desai@sharmapatel.co.in',
  username: 'vikram.desai',
  fullName: 'Vikram Desai',
  firstName: 'Vikram',
  lastName: 'Desai',
  phone: '9123456789',
  avatarUrl: undefined,
  tenantId: mediumTenant.tenantId,
  tenantName: mediumTenant.tenantName,
  role: 'partner',
  userCategory: 'ca_staff',
  isActive: true,
  isVerified: true,
  failedLogins: 0,
  lastLoginAt: '2024-12-20T08:45:00Z',
  preferences: { theme: 'light', language: 'en', timezone: 'Asia/Kolkata' },
  createdAt: '2023-07-01T09:00:00Z',
  updatedAt: '2024-12-20T08:45:00Z',
}

export const mediumPartner2: User = {
  userId: 'user-medium-004',
  email: 'sneha.reddy@sharmapatel.co.in',
  username: 'sneha.reddy',
  fullName: 'Sneha Reddy',
  firstName: 'Sneha',
  lastName: 'Reddy',
  phone: '9234567890',
  avatarUrl: undefined,
  tenantId: mediumTenant.tenantId,
  tenantName: mediumTenant.tenantName,
  role: 'partner',
  userCategory: 'ca_staff',
  isActive: true,
  isVerified: true,
  failedLogins: 0,
  lastLoginAt: '2024-12-18T14:20:00Z',
  preferences: { theme: 'light', language: 'en', timezone: 'Asia/Kolkata' },
  createdAt: '2023-08-15T10:00:00Z',
  updatedAt: '2024-12-18T14:20:00Z',
}

export const mediumAssistant1: User = {
  userId: 'user-medium-005',
  email: 'amit.verma@sharmapatel.co.in',
  username: 'amit.verma',
  fullName: 'Amit Verma',
  firstName: 'Amit',
  lastName: 'Verma',
  phone: '9345678901',
  avatarUrl: undefined,
  tenantId: mediumTenant.tenantId,
  tenantName: mediumTenant.tenantName,
  role: 'assistant',
  userCategory: 'ca_staff',
  isActive: true,
  isVerified: true,
  failedLogins: 0,
  lastLoginAt: '2024-12-20T09:00:00Z',
  preferences: { theme: 'light', language: 'en', timezone: 'Asia/Kolkata' },
  createdAt: '2024-01-10T09:00:00Z',
  updatedAt: '2024-12-20T09:00:00Z',
}

export const mediumStaff1: User = {
  userId: 'user-medium-006',
  email: 'deepa.nair@sharmapatel.co.in',
  username: 'deepa.nair',
  fullName: 'Deepa Nair',
  firstName: 'Deepa',
  lastName: 'Nair',
  phone: '9456789012',
  avatarUrl: undefined,
  tenantId: mediumTenant.tenantId,
  tenantName: mediumTenant.tenantName,
  role: 'staff',
  userCategory: 'ca_staff',
  isActive: true,
  isVerified: true,
  failedLogins: 0,
  lastLoginAt: '2024-12-19T11:30:00Z',
  preferences: { theme: 'light', language: 'en', timezone: 'Asia/Kolkata' },
  createdAt: '2024-03-01T09:00:00Z',
  updatedAt: '2024-12-19T11:30:00Z',
}

export const mediumResearchAssistant: User = {
  userId: 'user-medium-008',
  email: 'meera.iyer@sharmapatel.co.in',
  username: 'meera.iyer',
  fullName: 'Meera Iyer',
  firstName: 'Meera',
  lastName: 'Iyer',
  phone: '9456789013',
  tenantId: mediumTenant.tenantId,
  tenantName: mediumTenant.tenantName,
  role: 'research_assistant',
  userCategory: 'ca_staff',
  isActive: true,
  isVerified: true,
  failedLogins: 0,
  preferences: { theme: 'light', language: 'en', timezone: 'Asia/Kolkata' },
  createdAt: '2024-03-02T09:00:00Z',
  updatedAt: '2024-12-19T11:30:00Z',
}

export const mediumClient1: User = {
  userId: 'user-medium-007',
  email: 'client.ravi@gmail.com',
  username: undefined,
  fullName: 'Ravi Krishnan',
  firstName: 'Ravi',
  lastName: 'Krishnan',
  phone: '9567890123',
  avatarUrl: undefined,
  tenantId: mediumTenant.tenantId,
  tenantName: mediumTenant.tenantName,
  role: 'individual_client',
  userCategory: 'client',
  isActive: true,
  isVerified: true,
  failedLogins: 0,
  lastLoginAt: '2024-12-15T10:00:00Z',
  preferences: { theme: 'light', language: 'en', timezone: 'Asia/Kolkata' },
  createdAt: '2024-06-01T09:00:00Z',
  updatedAt: '2024-12-15T10:00:00Z',
}

export const mediumBusinessOwnerClient: User = {
  ...mediumClient1,
  userId: 'user-medium-009',
  email: 'business.owner@example.com',
  fullName: 'Bhavna Shah',
  firstName: 'Bhavna',
  lastName: 'Shah',
  role: 'business_owner_client',
}

export const mediumAuthorizedClient: User = {
  ...mediumClient1,
  userId: 'user-medium-010',
  email: 'authorized.client@example.com',
  fullName: 'Arjun Rao',
  firstName: 'Arjun',
  lastName: 'Rao',
  role: 'authorized_client',
}

// ============================================================================
// MOCK USERS - LARGE TENANT (unlimited users - sample)
// ============================================================================

export const largeOwner: User = {
  userId: 'user-large-001',
  email: 'suresh.mehta@mehtallp.com',
  username: 'suresh.mehta',
  fullName: 'Suresh Mehta',
  firstName: 'Suresh',
  lastName: 'Mehta',
  phone: '9876001234',
  avatarUrl: undefined,
  tenantId: largeTenant.tenantId,
  tenantName: largeTenant.tenantName,
  role: 'owner',
  userCategory: 'ca_staff',
  isActive: true,
  isVerified: true,
  failedLogins: 0,
  lastLoginAt: '2024-12-20T08:00:00Z',
  preferences: { theme: 'dark', language: 'en', timezone: 'Asia/Kolkata' },
  createdAt: '2022-01-10T08:00:00Z',
  updatedAt: '2024-12-20T08:00:00Z',
}

export const largeAdmin: User = {
  userId: 'user-large-002',
  email: 'kavita.joshi@mehtallp.com',
  username: 'kavita.joshi',
  fullName: 'Kavita Joshi',
  firstName: 'Kavita',
  lastName: 'Joshi',
  phone: '9876002345',
  avatarUrl: undefined,
  tenantId: largeTenant.tenantId,
  tenantName: largeTenant.tenantName,
  role: 'admin',
  userCategory: 'ca_staff',
  isActive: true,
  isVerified: true,
  failedLogins: 0,
  lastLoginAt: '2024-12-19T17:00:00Z',
  preferences: { theme: 'light', language: 'en', timezone: 'Asia/Kolkata' },
  createdAt: '2022-02-01T09:00:00Z',
  updatedAt: '2024-12-19T17:00:00Z',
}

// ============================================================================
// ALL USERS COLLECTION
// ============================================================================

export const mockUsersSolo: User[] = [soloOwner]

export const mockUsersMedium: User[] = [
  mediumOwner,
  mediumAdmin,
  mediumPartner1,
  mediumPartner2,
  mediumAssistant1,
  mediumStaff1,
  mediumResearchAssistant,
  mediumClient1,
  mediumBusinessOwnerClient,
  mediumAuthorizedClient,
]

export const mockUsersLarge: User[] = [largeOwner, largeAdmin]

const platformUser = (
  role: 'cml_admin' | 'cml_monitor' | 'cml_operations',
  suffix: string,
  name: string
): User => ({
  userId: `platform-${suffix}`,
  email: `${suffix}@cml.example`,
  username: suffix,
  fullName: name,
  firstName: name.split(' ')[0],
  lastName: name.split(' ').slice(1).join(' '),
  tenantId: '',
  role,
  userCategory: 'system',
  isActive: true,
  isVerified: true,
  failedLogins: 0,
  preferences: { theme: 'light', language: 'en', timezone: 'Asia/Kolkata' },
  createdAt: '2026-09-15T00:00:00Z',
  updatedAt: '2026-09-15T00:00:00Z',
})

export const platformAdmin = platformUser('cml_admin', 'admin', 'CML Admin')
export const platformMonitor = platformUser('cml_monitor', 'monitor', 'CML Monitor')
export const platformOperations = platformUser('cml_operations', 'operations', 'CML Operations')

export const mockUsers: User[] = [
  ...mockUsersSolo,
  ...mockUsersMedium,
  ...mockUsersLarge,
  platformAdmin,
  platformMonitor,
  platformOperations,
]

// ============================================================================
// MOCK INVITATIONS
// ============================================================================

export const mockInvitations: UserInvitation[] = [
  {
    invitationId: 'inv-001',
    tenantId: mediumTenant.tenantId,
    email: 'newuser@example.com',
    fullName: 'New User',
    role: 'assistant',
    status: 'pending',
    invitedBy: mediumOwner.userId,
    invitedByName: mediumOwner.fullName,
    message: 'Welcome to Sharma & Patel!',
    token: 'inv-token-001-abc123',
    expiresAt: '2025-01-27T00:00:00Z',
    createdAt: '2024-12-20T10:00:00Z',
  },
  {
    invitationId: 'inv-002',
    tenantId: mediumTenant.tenantId,
    email: 'expired@example.com',
    role: 'staff',
    status: 'expired',
    invitedBy: mediumAdmin.userId,
    invitedByName: mediumAdmin.fullName,
    token: 'inv-token-002-expired',
    expiresAt: '2024-12-01T00:00:00Z',
    createdAt: '2024-11-24T10:00:00Z',
  },
]

// ============================================================================
// AUTH USER CONVERSION
// ============================================================================

/**
 * Convert User to AuthUser for login response
 */
export function userToAuthUser(user: User, planCode: string = 'starter'): AuthUser {
  const tenant =
    user.tenantId === soloTenant.tenantId
      ? soloTenant
      : user.tenantId === mediumTenant.tenantId
        ? mediumTenant
        : largeTenant

  return {
    id: user.userId,
    email: user.email,
    username: user.username,
    firstName: user.firstName || user.fullName.split(' ')[0],
    lastName: user.lastName || user.fullName.split(' ').slice(1).join(' '),
    fullName: user.fullName,
    role: user.role,
    status: user.isActive === true ? 'active' : user.isActive === false ? 'inactive' : 'unknown',
    isVerified: user.isVerified,
    tenantId: user.tenantId,
    tenantCode: tenant.tenantCode,
    tenantName: tenant.tenantName,
    tenantType: tenant.tenantType,
    planCode: planCode as 'starter' | 'professional' | 'enterprise' | 'trial',
    avatarUrl: user.avatarUrl,
    phone: user.phone,
    lastLoginAt: user.lastLoginAt,
    createdAt: user.createdAt,
    updatedAt: user.updatedAt,
  }
}

// ============================================================================
// MOCK AUTH DATA (for login)
// ============================================================================

/**
 * Demo credentials for testing
 * All passwords: password123
 */
export const mockCredentials: Array<{
  email: string
  password: string
  user: User
  planCode: string
}> = [
  // Solo tenant
  {
    email: 'rajesh.kumar@rkassociates.in',
    password: 'password123',
    user: soloOwner,
    planCode: 'starter',
  },
  // Medium tenant
  {
    email: 'anil.sharma@sharmapatel.co.in',
    password: 'password123',
    user: mediumOwner,
    planCode: 'professional',
  },
  {
    email: 'priya.patel@sharmapatel.co.in',
    password: 'password123',
    user: mediumAdmin,
    planCode: 'professional',
  },
  {
    email: 'vikram.desai@sharmapatel.co.in',
    password: 'password123',
    user: mediumPartner1,
    planCode: 'professional',
  },
  {
    email: 'amit.verma@sharmapatel.co.in',
    password: 'password123',
    user: mediumAssistant1,
    planCode: 'professional',
  },
  {
    email: 'deepa.nair@sharmapatel.co.in',
    password: 'password123',
    user: mediumStaff1,
    planCode: 'professional',
  },
  {
    email: 'meera.iyer@sharmapatel.co.in',
    password: 'password123',
    user: mediumResearchAssistant,
    planCode: 'professional',
  },
  {
    email: 'client.ravi@gmail.com',
    password: 'password123',
    user: mediumClient1,
    planCode: 'professional',
  },
  {
    email: 'business.owner@example.com',
    password: 'password123',
    user: mediumBusinessOwnerClient,
    planCode: 'professional',
  },
  {
    email: 'authorized.client@example.com',
    password: 'password123',
    user: mediumAuthorizedClient,
    planCode: 'professional',
  },
  // Large tenant
  {
    email: 'suresh.mehta@mehtallp.com',
    password: 'password123',
    user: largeOwner,
    planCode: 'enterprise',
  },
  {
    email: 'kavita.joshi@mehtallp.com',
    password: 'password123',
    user: largeAdmin,
    planCode: 'enterprise',
  },
  {
    email: 'admin@cml.example',
    password: 'password123',
    user: platformAdmin,
    planCode: 'enterprise',
  },
  {
    email: 'monitor@cml.example',
    password: 'password123',
    user: platformMonitor,
    planCode: 'enterprise',
  },
  {
    email: 'operations@cml.example',
    password: 'password123',
    user: platformOperations,
    planCode: 'enterprise',
  },
]

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Mock login function
 */
export function mockLogin(
  email: string,
  password: string
): { user: AuthUser; token: string; refreshToken: string; sessionId: string } | null {
  const credential = mockCredentials.find(
    c => c.email.toLowerCase() === email.toLowerCase() && c.password === password
  )

  if (!credential) {
    return null
  }

  const authUser = userToAuthUser(credential.user, credential.planCode)
  const sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

  return {
    user: authUser,
    token: `mock-token-${credential.user.userId}-${Date.now()}`,
    refreshToken: `mock-refresh-${credential.user.userId}-${Date.now()}`,
    sessionId,
  }
}

/**
 * Validate token (mock)
 */
export function mockValidateToken(token: string): AuthUser | null {
  // Token format: mock-token-{userId}-{timestamp}
  const parts = token.split('-')
  if (parts.length < 4 || parts[0] !== 'mock' || parts[1] !== 'token') {
    return null
  }

  const userId = parts.slice(2, -1).join('-')
  const user = mockUsers.find(u => u.userId === userId)

  if (!user) {
    return null
  }

  const credential = mockCredentials.find(c => c.user.userId === userId)
  return userToAuthUser(user, credential?.planCode || 'starter')
}

/**
 * Get users by tenant
 */
export function getUsersByTenant(tenantId: string): User[] {
  if (!tenantId) return []
  return mockUsers.filter(
    u => u.tenantId === tenantId && !u.role.startsWith('cml_') && u.userCategory !== 'system'
  )
}

/**
 * Get users by role
 */
export function getUsersByRole(role: UserRole): User[] {
  return mockUsers.filter(u => u.role === role)
}

/**
 * Get user by ID
 */
export function getUserById(userId: string): User | undefined {
  return mockUsers.find(u => u.userId === userId)
}

/**
 * Get user by email
 */
export function getUserByEmail(email: string): User | undefined {
  return mockUsers.find(u => u.email.toLowerCase() === email.toLowerCase())
}

/**
 * Get invitation by token
 */
export function getInvitationByToken(token: string): UserInvitation | undefined {
  return mockInvitations.find(i => i.token === token)
}

/**
 * Get pending invitations for tenant
 */
export function getPendingInvitations(tenantId: string): UserInvitation[] {
  return mockInvitations.filter(i => i.tenantId === tenantId && i.status === 'pending')
}

// ============================================================================
// DEFAULT DEMO USER
// ============================================================================

export const DEFAULT_DEMO_USER = mediumOwner
export const DEFAULT_DEMO_AUTH_USER = userToAuthUser(mediumOwner, 'professional')
